/**
 * Nota de anomalia (0 a 100) do equipamento, da unidade e da cidade — Nexora Grid (frontend, GUS-1502).
 *
 * Criado em: 18/10/2026 · Última modificação: 18/10/2026 · Versão: 1.0.0
 *
 * Sprint 15 (CCO dinâmico): a barra lateral do card mostra de longe onde agir primeiro.
 * - Pior problema do equipamento: alarme não reconhecido ou medida fora da faixa (prioridade ISA-101) e dado velho.
 * - Prioridade 1 → 95 (crítico ◆), 2 → 68 (alto ▲), 3 → 42 e 4 → 30 (atenção ●); sem comunicação → 55 (violeta hachurado).
 *   O alarme pesa 2 pontos a mais que a medida fora da faixa de mesma prioridade (já virou evento no hub).
 * - Cada problema a mais soma 1 ponto, sem passar do teto do nível (a cor nunca muda pela soma).
 * - A nota da unidade e da cidade é a pior dos seus equipamentos. Abaixo de 25 é normal (cinza-verde discreto).
 * Módulo puro (sem React): testado em `testes/anomalia.test.mjs`.
 */
import type { Prioridade } from "./medidas";

export type Nivel = "normal" | "atencao" | "alto" | "critico" | "sem_comunicacao";
export type Nota = { nota: number; nivel: Nivel; motivo: string; problemas: number };
export type Problema = { origem: "alarme" | "medida"; prioridade: Prioridade; motivo: string };

const BASE: Record<Prioridade, number> = { 1: 93, 2: 66, 3: 40, 4: 28 };
const NIVEL: Record<Prioridade, Nivel> = { 1: "critico", 2: "alto", 3: "atencao", 4: "atencao" };
const TETO: Record<Nivel, number> = { critico: 100, alto: 79, atencao: 49, sem_comunicacao: 59, normal: 0 };
export const NOTA_SEM_COMUNICACAO = 55;
export const LIMIAR_ANOMALIA = 25;

export const NORMAL: Nota = { nota: 0, nivel: "normal", motivo: "", problemas: 0 };
export const FORMA_NIVEL: Record<Nivel, string> = { normal: "", atencao: "●", alto: "▲", critico: "◆", sem_comunicacao: "⟂" };
export const NOME_NIVEL: Record<Nivel, string> = { normal: "normal", atencao: "atenção", alto: "alto", critico: "crítico", sem_comunicacao: "sem comunicação" };

/** Nota do equipamento a partir dos seus problemas e do dado velho. */
export function notaEquipamento(problemas: Problema[], semComunicacao = false): Nota {
  const candidatos: Nota[] = problemas.map((p) => ({
    nota: BASE[p.prioridade] + (p.origem === "alarme" ? 2 : 0), nivel: NIVEL[p.prioridade], motivo: p.motivo, problemas: 0,
  }));
  if (semComunicacao) candidatos.push({ nota: NOTA_SEM_COMUNICACAO, nivel: "sem_comunicacao", motivo: "sem comunicação (dado velho)", problemas: 0 });
  if (!candidatos.length) return NORMAL;
  const pior = candidatos.reduce((a, b) => (b.nota > a.nota ? b : a));
  const total = candidatos.length;
  return { ...pior, nota: Math.min(TETO[pior.nivel], pior.nota + (total - 1)), problemas: total };
}

/** Nota de um conjunto (unidade, cidade): a pior; `problemas` soma os equipamentos com anomalia. */
export function piorNota(notas: Nota[]): Nota {
  const pior = notas.reduce((a, b) => (b.nota > a.nota ? b : a), NORMAL);
  return { ...pior, problemas: notas.filter(anomalo).length };
}

export const anomalo = (n: Nota) => n.nota >= LIMIAR_ANOMALIA;

/** Texto curto para title/aria: "◆ crítico 95 · nível crítico (< 20%)". */
export function descreverNota(n: Nota): string {
  if (!anomalo(n)) return "normal";
  return `${FORMA_NIVEL[n.nivel]} ${NOME_NIVEL[n.nivel]} ${n.nota}${n.motivo ? ` · ${n.motivo}` : ""}`;
}
